import { useEffect, useState } from "react";

const isValid = <T extends string>(
  options: readonly T[],
  param: string
): param is T => {
  return options.some((o) => o === param);
};

type Key =
  | "eco"
  | "eco2"
  | "year"
  | "sector"
  | "sub-sector"
  | "toggle"
  | "toggle2"
  | "product"
  | "dir"
  | "dir2";

export const filterAfterChangeEventKey = "global-flows-filter-after-change";

const updateValue = (key: Key, value: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set(key, value || "");
  window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
  document.dispatchEvent(new Event(filterAfterChangeEventKey));
};

export function useFilter<T extends string>(
  key: Key,
  options: readonly T[],
  defaultOptionArg?: T
) {
  const params = new URLSearchParams(window.location.search);
  const param = params.get(key) || "";

  const defaultOption =
    (defaultOptionArg && options.includes(defaultOptionArg)
      ? defaultOptionArg
      : key === "eco" || key === "eco2"
      ? options.find((o) => o === "wld")
      : key === "sector"
      ? options.find((o) => o === "0ag") || options.find((o) => o === "0mg")
      : null) || options[0];

  const [value, setValue] = useState(() => {
    if (!options.length || !isValid(options, param)) {
      return defaultOption;
    }

    return param;
  });

  // Make sure we still have the most recent value in state when filters change,
  // i.e. if there are two instances of this hook with the same key on a page.
  useEffect(() => {
    const onFilterChange = () => {
      const currentParams = new URLSearchParams(window.location.search);
      const currentParam = currentParams.get(key) || "";

      if (currentParam !== value && isValid(options, currentParam)) {
        setValue(currentParam);
      }
    };

    document.addEventListener(filterAfterChangeEventKey, onFilterChange);
    return () => {
      document.removeEventListener(filterAfterChangeEventKey, onFilterChange);
    };
  });

  useEffect(() => {
    if (options.length && !isValid(options, param)) {
      updateValue(key, defaultOption);
    }
  }, [options, param, defaultOption, key]);

  useEffect(() => {
    if (param !== value && isValid(options, param)) {
      setValue(param);
    }
  }, [param, value, options]);

  return {
    value: value,
    onChange: (newValue: string) => {
      const newParam = isValid(options, newValue) ? newValue : defaultOption;
      updateValue(key, newParam);
    },
  };
}
